// src/services/drawApi.js
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../api/baseQuery';

// Videos Status Enum
export const VideosStatus = {
  Draft: 0,
  Active: 1,
  Completed: 2,
  Archived: 3,
};

// Question Type Enum (matching survey structure)
export const QuestionType = {
  MultipleChoice: 0,
  SingleChoice: 1,
  YesNo: 2,
  Rating: 3,
};

// Using centralized baseQueryWithReauth from api/baseQuery.js
export const videosApi = createApi({
  reducerPath: 'videosApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Videos', 'VideosStats'],
  endpoints: (builder) => ({
    // Get all video sets (admin sees all, tenant admin sees only their own)
    getVideoss: builder.query({
      query: ({ pageNumber = 1, pageSize = 8, keyword = '', sortBy, sortOrder } = {}) => {
        // Backend expects "page" (0-indexed) instead of "pageNumber" (1-indexed)
        const page = pageNumber - 1; // Convert 1-indexed to 0-indexed
        
        const queryParams = {
          page: page.toString(),
          pageSize: pageSize.toString(),
          ...(keyword && keyword.trim() && { keyword: keyword.trim() }),
          ...(sortBy && { sortBy }),
          ...(sortOrder && { sortOrder }),
        };

        console.log('🔍 VideoSet API Query Params:', queryParams);
        
        return {
          url: '/videoset/list',
          method: 'GET',
          params: queryParams,
        };
      },
      transformResponse: (response) => {
        console.log('✅ VideoSet List API Response:', response);
        
        if (!response) {
          return {
            value: {
              videoSets: [],
              totalCount: 0,
            },
            isSuccess: false,
            errors: [],
            validationErrors: [],
          };
        }

        return {
          value: {
            videoSets: response.value?.videoSets || [],
            totalCount: response.value?.totalCount || 0,
          },
          isSuccess: response.isSuccess,
          errors: response.errors || [],
          validationErrors: response.validationErrors || [],
        };
      },
      providesTags: (result) =>
        result?.value?.videoSets
          ? [
              ...result.value.videoSets.map(({ id }) => ({ type: 'Videos', id })),
              { type: 'Videos', id: 'LIST' },
            ]
          : [{ type: 'Videos', id: 'LIST' }],
    }),

    // Get single videos by ID
    getVideosById: builder.query({
      query: (id) => ({
        url: `/videos/${id}`,
        method: 'GET',
      }),
      transformResponse: (response) => {
        console.log('✅ Videos Detail API Response:', response);
        
        if (!response) {
          return null;
        }

        return {
          value: response.value || null,
          isSuccess: response.isSuccess,
          errors: response.errors || [],
          validationErrors: response.validationErrors || [],
        };
      },
      providesTags: (result, error, id) => [{ type: 'Videos', id }],
    }),

    // Create video set
    createVideos: builder.mutation({
      query: (videoSetData) => {
        console.log('🔥 VideoSet Create API - Request Data:', videoSetData);
        
        // Expected structure:
        // {
        //   name: string,
        //   description: string,
        //   tenantIds: string[],
        //   items: [{ title, description, tempVideoUrl, order }],
        //   categoryId: string (optional),
        //   tags: string[] (optional)
        // }
        
        return {
          url: '/videoset/create',
          method: 'POST',
          body: videoSetData,
        };
      },
      transformResponse: (response) => {
        console.log('✅ VideoSet Create API Response:', response);
        
        if (!response) {
          return {
            isSuccess: true,
            message: 'Video seti başarıyla oluşturuldu',
            value: true
          };
        }
        
        return response;
      },
      transformErrorResponse: (response) => {
        console.error('❌ VideoSet Create API Error:', response);
        return {
          status: response.status,
          message: response.data?.message || 'Video seti oluşturulurken hata oluştu',
          errors: response.data?.errors || [],
          validationErrors: response.data?.validationErrors || []
        };
      },
      invalidatesTags: [{ type: 'Videos', id: 'LIST' }],
    }),

    // Update videos
    updateVideos: builder.mutation({
      query: ({ id, ...videosData }) => {
        console.log('🔥 Videos Update API - Request Data:', { id, ...videosData });
        return {
          url: `/videos/update`,
          method: 'PUT',
          body: { id, ...videosData },
        };
      },
      transformResponse: (response, meta) => {
        console.log('✅ Videos Update API Response:', response);
        console.log('📡 Videos Update Meta:', meta);
        
        // Handle empty response (204 No Content)
        if (meta?.response?.status === 204) {
          return {
            isSuccess: true,
            message: 'Videos modülü başarıyla güncellendi',
            value: true
          };
        }
        
        // Handle null response
        if (!response) {
          return {
            isSuccess: true,
            message: 'Videos modülü başarıyla güncellendi',
            value: true
          };
        }
        
        return response;
      },
      transformErrorResponse: (response) => {
        console.error('❌ Videos Update API Error:', response);
        return {
          status: response.status,
          message: response.data?.message || 'Videos güncellenirken hata oluştu',
          errors: response.data?.errors || [],
          validationErrors: response.data?.validationErrors || []
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: 'Videos', id },
        { type: 'Videos', id: 'LIST' },
      ],
    }),

    // Delete videos
    deleteVideos: builder.mutation({
      query: (id) => {
        console.log('🔥 Videos Delete API - ID:', id);
        return {
          url: `/videos/${id}`,
          method: 'DELETE',
        };
      },
      transformResponse: (response, meta) => {
        console.log('✅ Videos Delete API Response:', response);
        
        // Handle 204 No Content
        if (meta?.response?.status === 204) {
          return {
            isSuccess: true,
            message: 'Videos modülü başarıyla silindi',
          };
        }
        
        return response || { isSuccess: true, message: 'Silme işlemi başarılı' };
      },
      transformErrorResponse: (response) => {
        console.error('❌ Videos Delete API Error:', response);
        return {
          status: response.status,
          message: response.data?.message || 'Videos silinirken hata oluştu',
          errors: response.data?.errors || [],
        };
      },
      invalidatesTags: [{ type: 'Videos', id: 'LIST' }],
    }),

    // Upload temp video (temporary upload before final save)
    uploadTempVideo: builder.mutation({
      query: (file) => {
        console.log('🔥 Temp Video Upload API - Building Request');
        console.log('📁 File:', file);

        const formData = new FormData();
        formData.append('file', file);

        return {
          url: '/videoset/upload-temp',
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      transformResponse: (response) => {
        console.log('✅ Temp Video Upload API Response:', response);
        
        // Expected response structure:
        // {
        //   "url": "string",
        //   "fileName": "string",
        //   "fileSize": 0
        // }
        
        if (!response) {
          throw new Error('Empty response from server');
        }

        // Extract data from response
        const url = response.url || response.value?.url;
        const fileName = response.fileName || response.value?.fileName;
        const fileSize = response.fileSize || response.value?.fileSize;
        
        console.log('🔍 Temp Video Upload Extraction:', {
          url,
          fileName,
          fileSize
        });
        
        // Validate required fields
        if (!url) {
          console.error('❌ Temp Video Upload - Missing URL in response');
          throw new Error('Upload successful but missing URL in response');
        }
        
        // Return normalized response
        return {
          url,
          fileName,
          fileSize,
          isSuccess: true
        };
      },
      transformErrorResponse: (response) => {
        console.error('❌ Temp Video Upload API Error:', response);
        return {
          status: response.status,
          message: response.data?.message || 'Video yüklenirken hata oluştu',
          errors: response.data?.errors || [],
          validationErrors: response.data?.validationErrors || []
        };
      },
    }),

    // Upload videos video (legacy - kept for backward compatibility)
    addVideosVideo: builder.mutation({
      query: (formData) => {
        console.log('🔥 Videos Video API - Building Request');
        console.log('📁 API FormData:', formData);

        return {
          url: '/videos/upload-video',
          method: 'POST',
          body: formData,
          formData: true,
        };
      },
      transformResponse: (response) => {
        console.log('✅ Videos Video API Response:', response);
        
        // Extract URL from response
        let extractedUrl = null;
        
        if (response?.value?.url) {
          extractedUrl = response.value.url;
        } else if (response?.value && typeof response.value === 'string') {
          extractedUrl = response.value;
        } else if (response?.url) {
          extractedUrl = response.url;
        } else if (response?.data?.url) {
          extractedUrl = response.data.url;
        }
        
        console.log('🔍 Video URL Extraction:', {
          extractedUrl: extractedUrl,
          isSuccess: response?.isSuccess
        });
        
        // Validate success response has URL
        if (response?.isSuccess && !extractedUrl) {
          console.error('❌ Video Upload - Successful response missing URL');
          throw new Error('Upload successful but missing URL in response');
        }
        
        // Return normalized response
        return {
          ...response,
          value: extractedUrl || response?.value,
          isSuccess: response?.isSuccess || false,
          errors: response?.errors || [],
          validationErrors: response?.validationErrors || []
        };
      },
      transformErrorResponse: (response) => {
        console.error('❌ Videos Video API Error:', response);
        return response;
      },
      invalidatesTags: ['Videos'],
    }),

    // Get videos statistics
    getVideosStats: builder.query({
      query: () => ({
        url: '/videos/statistics',
        method: 'GET',
      }),
      transformResponse: (response) => {
        console.log('✅ Videos Stats API Response:', response);
        
        if (!response) {
          return {
            value: null,
            isSuccess: false,
            errors: [],
          };
        }

        return {
          value: response.value || null,
          isSuccess: response.isSuccess,
          errors: response.errors || [],
        };
      },
      providesTags: ['VideosStats'],
    }),
  }),
});

export const {
  useGetVideossQuery,
  useGetVideosByIdQuery,
  useCreateVideosMutation,
  useUpdateVideosMutation,
  useDeleteVideosMutation,
  useUploadTempVideoMutation,
  useAddVideosVideoMutation,
  useGetVideosStatsQuery,
} = videosApi;

export const {
  reducer: videosReducer,
  middleware: videosMiddleware,
  reducerPath: videosReducerPath,
} = videosApi;
